import type { ChooseProduct, OrderFinancialVO } from "../OrderForms/orders";
import {
  ZUU_FNA_CANONICAL_QUESTION_ORDER,
  getZuuFnaQuestionValueFromList,
} from "./zuuFnaFieldMap";

export type ZuuQuestionBasedFnaPayload = {
  financialAnalysis?: any[];
  premiumFinanceAnalysis?: any[];
  suggestionsAndReasons?: any[];
  chooseProducts?: ChooseProduct[];
  isPremiumFinanced?: boolean;
  onlyProvideOneProduct?: boolean;
  onlyProvideOneProductReason?: string;
};

const ZUU_FNA_CANONICAL_QUESTION_SET = new Set(ZUU_FNA_CANONICAL_QUESTION_ORDER);

function filterZuuFinancialAnalysisQuestions(questions: any[]) {
  const canonicalQuestions = (questions || []).filter((question) =>
    ZUU_FNA_CANONICAL_QUESTION_SET.has(question?.question),
  );

  const objectives =
    (getZuuFnaQuestionValueFromList(
      canonicalQuestions,
      "insurancePurchaseObjectives",
      "CHECKBOX",
    ) as string[]) || [];
  const hasLongTermGoal =
    objectives.includes("C") || objectives.includes("D") || objectives.includes("E");
  const otherObjectiveOption = getZuuFnaQuestionValueFromList(
    canonicalQuestions,
    "insurancePurchaseObjectives_F",
    "RADIO",
  );
  const premiumPaymentPeriodRange = getZuuFnaQuestionValueFromList(
    canonicalQuestions,
    "willingPremiumPaymentPeriodRange",
    "RADIO",
  );

  const retained = new Set<string>([
    "insurancePurchaseObjectives",
    "premiumToDisposableIncomeRatio",
    "premiumToLiquidAssetsRatio",
    "willingPremiumPaymentPeriodRange",
    "policyTargetExpectedTime",
  ]);

  if (objectives.includes("A")) {
    retained.add("insurancePurchaseObjectives_A_ProposedInsured");
    retained.add("insurancePurchaseObjectives_A_ProposedInsured_A_amount");
    retained.add("insurancePurchaseObjectives_A_ProposedInsured_B_amount");
  }

  if (objectives.includes("B")) {
    retained.add("insurancePurchaseObjectives_B");
    retained.add("insurancePurchaseObjectives_B_C_ProposedInsured");
    retained.add("insurancePurchaseObjectives_B_C_ProposedInsured_A_amount");
    retained.add("insurancePurchaseObjectives_B_C_ProposedInsured_B_amount");
  }

  if (objectives.includes("E")) {
    retained.add("insurancePurchaseObjectives_E");
  }

  if (hasLongTermGoal) {
    retained.add("insurancePurchaseObjectives_CDE_ProposedInsured");
    retained.add("insurancePurchaseObjectives_CDE_ProposedInsured_A_amount");
    retained.add("insurancePurchaseObjectives_CDE_ProposedInsured_B_amount");
    retained.add("insurancePurchaseObjectives_CDE_year");
  }

  if (objectives.includes("F")) {
    retained.add("insurancePurchaseObjectives_F");
    if (otherObjectiveOption === "E") {
      retained.add("insurancePurchaseObjectives_F_E_remark");
    }
  }

  if (premiumPaymentPeriodRange === "G") {
    retained.add("willingPremiumPaymentPeriodRange_G_amount");
  }

  return canonicalQuestions.filter((question) => retained.has(question.question));
}


function filterZuuSuggestionsQuestions(questions: any[]) {
  const questionMap = new Map(
    (questions || [])
      .filter((question) => question?.question)
      .map((question) => [question.question, question]),
  );

  const reasonForTheRecommendation = questionMap.get("reasonForTheRecommendation");
  const reasonForTheRecommendationB = questionMap.get("reasonForTheRecommendation_B");
  const reasonForTheRecommendationB1Remark = questionMap.get(
    "reasonForTheRecommendation_B_B1_G_remark",
  );
  const reasonForTheRecommendationB2Remark = questionMap.get(
    "reasonForTheRecommendation_B_B2_E_remark",
  );

  const selectedReason = getZuuFnaQuestionValueFromList(
    questions,
    "reasonForTheRecommendation",
    "RADIO",
  );
  const selectedReasons =
    (getZuuFnaQuestionValueFromList(
      questions,
      "reasonForTheRecommendation_B",
      "CHECKBOX",
    ) as string[]) || [];

  const result = [];
  if (reasonForTheRecommendation) {
    result.push(reasonForTheRecommendation);
  }
  if (selectedReason === "B" && reasonForTheRecommendationB) {
    result.push(reasonForTheRecommendationB);
    if (
      selectedReasons.includes("B1_G") &&
      reasonForTheRecommendationB1Remark
    ) {
      result.push(reasonForTheRecommendationB1Remark);
    }
    if (
      selectedReasons.includes("B2_E") &&
      reasonForTheRecommendationB2Remark
    ) {
      result.push(reasonForTheRecommendationB2Remark);
    }
  }

  return result.filter((question) =>
    hasMeaningfulItems(question.type, question.items),
  );
}

function buildQuestion(
  question: string,
  type: "RADIO" | "CHECKBOX" | "INPUT",
  items: any[],
) {
  return {
    question,
    questionNumber: null,
    type,
    items,
  };
}

function hasMeaningfulItems(
  type: "RADIO" | "CHECKBOX" | "INPUT",
  items: any[],
) {
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }

  if (type === "INPUT") {
    return items.some((item) => {
      const value = item?.value;
      if (value === undefined || value === null) {
        return false;
      }
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return true;
    });
  }

  return items.some((item) => item?.value === true);
}

function hasInputValue(value: unknown) {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  return true;
}

function hasStrictPositiveNumber(value: unknown) {
  if (value === undefined || value === null || value === "") {
    return false;
  }
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value.replace(/,/g, "").trim())
        : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0;
}

function buildZuuPremiumFinanceAnalysis(raw: Partial<OrderFinancialVO>): any[] {
  if (!raw.isPremiumFinanced) {
    return [];
  }

  const premiumFinancingList = Array.isArray(raw.premiumFinancing)
    ? raw.premiumFinancing.filter(Boolean)
    : [];
  const repaymentSources = Array.isArray(raw.premiumFinancingRepaymentSources)
    ? raw.premiumFinancingRepaymentSources.filter(Boolean)
    : [];
  const willingToProvideKey =
    raw.premiumFinancingEstimateConsent === true
      ? "A"
      : raw.premiumFinancingEstimateConsent === false
        ? "B"
        : undefined;
  const firstPremium = willingToProvideKey === "A" ? premiumFinancingList[0] || {} : {};

  const result = [
    buildQuestion(
      "premiumFinancingRepaymentSource",
      "RADIO",
      repaymentSources[0] ? [{ key: repaymentSources[0], value: true }] : [],
    ),
    buildQuestion(
      "premiumFinancingRepaymentSource_D_remark",
      "INPUT",
      repaymentSources[0] === "D" && hasInputValue(raw.premiumFinancingRepaymentSourceOther)
        ? [{ key: "A", value: raw.premiumFinancingRepaymentSourceOther }]
        : [],
    ),
    buildQuestion(
      "outstandingMortgageAmount",
      "INPUT",
      raw.outstandingMortgageLoanAmount !== undefined &&
      raw.outstandingMortgageLoanAmount !== null &&
      raw.outstandingMortgageLoanAmount !== ""
        ? [{ key: "A", value: raw.outstandingMortgageLoanAmount }]
        : [],
    ),
    buildQuestion(
      "outstandingOtherLoansAmount",
      "INPUT",
      raw.outstandingOtherLoanAmount !== undefined &&
      raw.outstandingOtherLoanAmount !== null &&
      raw.outstandingOtherLoanAmount !== ""
        ? [{ key: "A", value: raw.outstandingOtherLoanAmount }]
        : [],
    ),
    buildQuestion(
      "willingToProvidePremiumFinancingInfo",
      "RADIO",
      willingToProvideKey ? [{ key: willingToProvideKey, value: true }] : [],
    ),
    buildQuestion(
      "willingToProvidePremiumFinancingInfo_A_nameOfLender",
      "INPUT",
      hasInputValue(firstPremium.lenderName)
        ? [{ key: "A", value: firstPremium.lenderName }]
        : [],
    ),
    buildQuestion(
      "willingToProvidePremiumFinancingInfo_A_loanAmount",
      "INPUT",
      hasStrictPositiveNumber(firstPremium.loanAmount)
        ? [{ key: "A", value: firstPremium.loanAmount }]
        : [],
    ),
    buildQuestion(
      "willingToProvidePremiumFinancingInfo_A_loanInterestRate",
      "INPUT",
      hasInputValue(firstPremium.loanInterestRate)
        ? [{ key: "A", value: firstPremium.loanInterestRate }]
        : [],
    ),
    buildQuestion(
      "willingToProvidePremiumFinancingInfo_A_loanTenor",
      "INPUT",
      hasInputValue(firstPremium.loanTerm)
        ? [{ key: "A", value: firstPremium.loanTerm }]
        : [],
    ),
    buildQuestion(
      "willingToProvidePremiumFinancingInfo_A_eachInstallment",
      "INPUT",
      hasInputValue(firstPremium.repaymentAmount)
        ? [{ key: "A", value: firstPremium.repaymentAmount }]
        : [],
    ),
    buildQuestion(
      "confirmedImportantNotes",
      "RADIO",
      raw.premiumFinancingImportantDisclosureConfirmed
        ? [{ key: "A", value: true }]
        : [],
    ),
    buildQuestion(
      "hasSufficientFundsToRepayLoan",
      "RADIO",
      raw.premiumFinancingRepaymentConfirmed
        ? [{ key: "A", value: true }]
        : [],
    ),
  ];

  return result.filter((question) =>
    hasMeaningfulItems(question.type, question.items),
  );
}

export function buildZuuQuestionBasedFnaPayload(input: {
  raw: Partial<OrderFinancialVO>;
  financial: Partial<OrderFinancialVO>;
  cleanedFinancialAnalysis: any[];
  cleanedSuggestionsAndReasons: any[];
}): ZuuQuestionBasedFnaPayload {
  const {
    raw,
    financial,
    cleanedFinancialAnalysis,
    cleanedSuggestionsAndReasons,
  } = input;

  return {
    financialAnalysis: filterZuuFinancialAnalysisQuestions(cleanedFinancialAnalysis),
    premiumFinanceAnalysis: buildZuuPremiumFinanceAnalysis(raw),
    suggestionsAndReasons: filterZuuSuggestionsQuestions(cleanedSuggestionsAndReasons),
    chooseProducts: raw.chooseProducts,
    isPremiumFinanced: raw.isPremiumFinanced ?? false,
    onlyProvideOneProduct: raw.onlyProvideOneProduct,
    onlyProvideOneProductReason: financial.onlyProvideOneProduct
      ? raw.onlyProvideOneProductReason
      : undefined,
  };
}
